import { DoorClosed, DoorClosedLocked, Plus, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ElectronicTable from "./electronic-table";
import PushButtonTable from "./push-button-table";
import {
  getElectronicLockboxes,
  getPushButtonLockbox,
  getAlldealforAdmin,
} from "../../utils/firebasefunctions";

function MainLockboxInventory() {
  const [activeTab, setActiveTab] = useState("electronic");
  const status = ["Available", "In Repair", "Lost"];
  const [electronicRows, setElectronicRows] = useState([]);
  const [pushButtonRows, setPushButtonRows] = useState([]);
  const [electronicData, setElectronicData] = useState([]);
  const [pushButtonData, setPushButtonData] = useState([]);
  // Listings to choose from when assigning a lockbox (the picker in each table).
  const [listings, setListings] = useState([]);
  // Free-text filter for the visible table (number / listing / notes / status).
  const [search, setSearch] = useState("");
  const tabs = [
    { id: "electronic", label: "Electronic", icon: DoorClosedLocked },
    { id: "pushButton", label: "Push Button", icon: DoorClosed },
  ];
  const childRef = useRef();

  const currentData =
    activeTab === "electronic" ? electronicData : pushButtonData;

  const handleAddRow = () => {
    childRef.current.addRow();
  };

  useEffect(() => {
    loadData();
    loadStats();
    loadListings();
  }, []);

  // Pull listings (deals with a property address) for the assign picker, newest
  // first. Lockboxes go on listings, so any deal with an address is a target.
  const loadListings = async () => {
    try {
      const all = (await getAlldealforAdmin("deals")) || [];
      const filtered = all
        .filter((d) => d?.propertyaddress && d?.createdat)
        .sort((a, b) => (b.createdat || 0) - (a.createdat || 0));
      setListings(filtered);
    } catch (error) {
      console.error("Listings loading error:", error);
    }
  };

  // Reload rows, stat counts, AND the listings snapshot after an assignment
  // write, so a follow-up assignment in the same session reads fresh deal state.
  const reload = async () => {
    await Promise.all([loadData(), loadStats(), loadListings()]);
  };

  const loadData = async () => {
    const [electronic, pushbutton] = await Promise.all([
      getElectronicLockboxes(),
      getPushButtonLockbox(),
    ]);

    setElectronicRows(electronic);
    setPushButtonRows(pushbutton);
  };

  const loadStats = async () => {
    try {
      const [electronic, pushbutton] = await Promise.all([
        getElectronicLockboxes(),
        getPushButtonLockbox(),
      ]);

      setElectronicData(electronic);
      setPushButtonData(pushbutton);
    } catch (error) {
      console.error("Stats loading error:", error);
    }
  };

  const totalCount = currentData.length;

  const getStatusCount = (data, status) => {
    return data.filter((r) => r.status === status).length;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">
          Lockbox Inventory
        </h3>
        <button
          className="bg-[#1E293B] text-white rounded-md font-medium hover:bg-[#0f172a] transition-colors duration-150 ease-in-out flex items-center gap-2 px-3 py-2"
          onClick={handleAddRow}
        >
          <Plus size={18} />
          Add Lockbox
        </button>
      </div>
      <div className="flex gap-2 bg-white p-2 rounded-2xl shadow overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
            }}
            className={`flex items-center justify-center gap-2 whitespace-nowrap px-4 py-2 rounded-lg font-semibold flex-shrink-0 transition-all ${
              activeTab === tab.id
                ? "bg-gradient-to-b from-[#0f172a] to-[#1E293B] text-white"
                : "bg-transparent text-gray-500 hover:bg-blue-50"
            }`}
          >
            {tab.icon && <tab.icon className="w-4 h-4" />}
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-[2%] gap-y-3 lg:gap-x-0 lg:justify-between mt-6">
        <div className="w-[48%] md:w-[32%] lg:w-[18%] bg-white border border-black/10 rounded-[10px] px-[18px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-sm text-gray-500 uppercase tracking-[0.08em] font-semibold mb-1">
            Total
          </div>
          <div className="text-2xl font-bold text-slate-900 leading-none">
            {totalCount}
          </div>
        </div>
        <div className="w-[48%] md:w-[32%] lg:w-[18%] bg-white border border-black/10 rounded-[10px] px-[18px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-sm text-gray-500 uppercase tracking-[0.08em] font-semibold mb-1">
            Available
          </div>
          <div className="text-2xl font-bold text-[#047857] leading-none">
            {getStatusCount(currentData, "Available")}
          </div>
        </div>
        <div className="w-[48%] md:w-[32%] lg:w-[18%] bg-white border border-black/10 rounded-[10px] px-[18px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-sm text-gray-500 uppercase tracking-[0.08em] font-semibold mb-1">
            Assigned
          </div>
          <div className="text-2xl font-bold text-[#4338ca] leading-none">
            {getStatusCount(currentData, "Assigned")}
          </div>
        </div>
        <div className="w-[48%] md:w-[32%] lg:w-[18%] bg-white border border-black/10 rounded-[10px] px-[18px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-sm text-gray-500 uppercase tracking-[0.08em] font-semibold mb-1">
            In Repair
          </div>
          <div className="text-2xl font-bold text-[#b45309] leading-none">
            {getStatusCount(currentData, "In Repair")}
          </div>
        </div>
        <div className="w-[48%] md:w-[32%] lg:w-[18%] bg-white border border-black/10 rounded-[10px] px-[18px] py-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <div className="text-sm text-gray-500 uppercase tracking-[0.08em] font-semibold mb-1">
            Lost
          </div>
          <div className="text-2xl font-bold text-[#b91c1c] leading-none">
            {getStatusCount(currentData, "Lost")}
          </div>
        </div>
      </div>
      <div className="mt-6 relative max-w-md">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search lockboxes by #, listing, notes, or status…"
          className="w-full border border-gray-300 rounded-lg pl-9 pr-9 py-2 text-sm outline-none focus:border-[#1E293B]"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
          >
            <X size={16} />
          </button>
        )}
      </div>
      <div className="mt-4">
        {activeTab === "electronic" ? (
          <ElectronicTable
            ref={childRef}
            rows={electronicRows}
            setRows={setElectronicRows}
            status={status}
            onClose={loadStats}
            onStatsUpdate={loadStats}
            listings={listings}
            reload={reload}
            search={search}
          />
        ) : (
          <PushButtonTable
            ref={childRef}
            rows={pushButtonRows}
            setRows={setPushButtonRows}
            status={status}
            onClose={loadStats}
            onStatsUpdate={loadStats}
            listings={listings}
            reload={reload}
            search={search}
          />
        )}
      </div>
    </div>
  );
}

export default MainLockboxInventory;
